// This component was ported from minted-components Panel component, and modified to work for a new mobile experience
// for both wedding and online invitation sites.
import { TRACKING } from "../../../../core/components/LaunchDarklyProvider";
import { useRouterNavigation } from "../../../../core/routing/routingHooks";
import SitePreviewContext from "../../SitePreviewContext";
import { FULL_HEIGHT, PANEL_HEIGHTS, SwipePanelView } from "./SectionHeights";
import {
  getPanelTransform,
  HEADER_HEIGHT,
  lockBodyScroll,
  SwipeDirection,
  swipePanelSharedStyles,
  unlockBodyScroll,
  useSwipeGesture,
} from "./swipePanelShared";
import { css } from "@emotion/react";
import {
  Icon,
  IconButton,
  IconToggleButton,
  styleUtils,
  theme,
} from "@minted/minted-components";
import React, {
  createContext,
  forwardRef,
  ReactNode,
  TransitionEventHandler,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export type SaveHandler = () => Promise<{
  success: boolean;
  hasValidationErrors?: boolean;
}>;

// Context for sharing panel state with action buttons
type SwipePanelContextType = {
  isOpen: boolean;
  close: () => void;
  transitionToPanelView: (view: SwipePanelView) => void;
  registerSaveHandler: (handler: SaveHandler | null) => void;
};

const SwipePanelContext = createContext<SwipePanelContextType>({
  close: () => {},
  isOpen: false,
  registerSaveHandler: () => {},
  transitionToPanelView: () => {},
});

export const useSwipePanelState = () => useContext(SwipePanelContext);

export enum PanelDisplayState {
  Open = "open",
  Closed = "closed",
  Partial = "partial",
}

export type SwipePanelRef = {
  close: () => void;
  open: (height?: number) => void;
  transitionToPanelView: (view: SwipePanelView) => void;
};

export type SwipePanelProps = {
  children: ReactNode;
  initialDisplayState: PanelDisplayState;
  initialPeek: number;
  onSwipingChange: (isSwiping: boolean) => void;
};

// SwipePanel-specific styles (uses shared styles from swipePanelShared.ts)
const swipePanelStyles = {
  addElementButtonContainer: css`
    bottom: ${styleUtils.rem(74)};
    position: fixed;
    right: ${theme.spacing.x5};
    // Children only need relative z-index to prevent overlapping with the panel
    z-index: 1;
  `,
  buttonContainer: css`
    button {
      width: ${styleUtils.rem(128)};
    }
  `,
  closedStateButtons: css`
    align-items: center;
    display: flex;
    flex-direction: row;
    height: ${styleUtils.rem(62)};
    justify-content: space-around;
  `,
  closedStateContainer: css`
    background: ${theme.colors.white};
    border-top: 1px solid ${theme.colors.gray300};
    bottom: 0;
    display: flex;
    flex-direction: column;
    left: 0;
    position: fixed;
    right: 0;
    // Children only need relative z-index to prevent overlapping with the panel
    z-index: 1;
  `,
  headerButton: css`
    align-items: center;
    background: none;
    border: 0;
    color: inherit;
    cursor: pointer;
    display: flex;
    flex: 1 0 auto;
    height: ${HEADER_HEIGHT}px;
    justify-content: center;
    width: 100%;

    &:after {
      background: ${theme.colors.gray500};
      border-radius: ${theme.spacing.base};
      content: "";
      height: ${theme.spacing.base};
      width: ${theme.spacing.x8};
    }
  `,
};

export const SwipePanel = forwardRef<SwipePanelRef, SwipePanelProps>(
  ({ children, initialDisplayState, initialPeek, onSwipingChange }, ref) => {
    const navigateTo = useRouterNavigation();
    const { triggerAddNewSection } = useContext(SitePreviewContext);
    const [displayState, setDisplayState] =
      useState<PanelDisplayState>(initialDisplayState);
    const [isToggleEnabled, setIsToggleEnabled] = useState<boolean>(true);
    const [isEditDesignActive, setIsEditDesignActive] = useState(false);
    const [isEventDetailsActive, setIsEventDetailsActive] = useState(false);

    const [currentPosition, setCurrentPosition] = useState<number>(
      initialDisplayState === PanelDisplayState.Closed
        ? initialPeek
        : window.innerHeight - 100,
    );

    const container = useRef<HTMLDivElement>(null);
    const panelContent = useRef<HTMLDivElement>(null);
    const saveHandlerRef = useRef<SaveHandler | null>(null);

    /**
     * Re-enables the toggle button after CSS transitions complete.
     * Prevents rapid clicking during panel animations.
     */
    const handleTransitionEnd: TransitionEventHandler = () => {
      setIsToggleEnabled(true);
    };

    const handleOpen = useCallback((height?: number) => {
      setDisplayState(PanelDisplayState.Open);
      const targetHeight = height ?? window.innerHeight - 100; // Use provided height or default to almost full height
      setCurrentPosition(targetHeight);
      setIsToggleEnabled(false);
      lockBodyScroll();
    }, []);

    const handleClose = useCallback(async () => {
      // If there's a save handler registered, call it first
      if (saveHandlerRef.current) {
        const result = await saveHandlerRef.current();

        // If validation errors, keep panel open
        if (!result.success && result.hasValidationErrors) {
          return;
        }

        // For network errors or success, proceed with close
        // (EditSection will handle goToPageView on success)
      }

      setDisplayState(PanelDisplayState.Closed);
      setCurrentPosition(initialPeek);
      setIsToggleEnabled(false);
      setIsEditDesignActive(false);
      setIsEventDetailsActive(false);
      saveHandlerRef.current = null;
      unlockBodyScroll();
    }, [initialPeek]);

    const transitionToHeight = useCallback((height: number) => {
      setDisplayState(PanelDisplayState.Open);
      setCurrentPosition(height);
      setIsToggleEnabled(false);
    }, []);

    const transitionToPanelView = useCallback(
      (view: SwipePanelView) => transitionToHeight(PANEL_HEIGHTS[view]),
      [transitionToHeight],
    );

    const registerSaveHandler = useCallback((handler: SaveHandler | null) => {
      saveHandlerRef.current = handler;
    }, []);

    // Cleanup body scroll lock when component unmounts
    useEffect(() => {
      return () => {
        unlockBodyScroll();
      };
    }, []);

    // Expose open and close methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        close: handleClose,
        open: handleOpen,
        transitionToPanelView,
      }),
      [handleClose, handleOpen, transitionToPanelView],
    );

    const handleToggle = () => {
      if (isToggleEnabled) {
        const halfwayPoint = (window.innerHeight - 100) / 2;

        if (currentPosition > halfwayPoint) {
          // Panel is MORE than halfway open → close it
          handleClose();
        } else {
          // Panel is LESS than halfway open → open it
          handleOpen(FULL_HEIGHT);
        }
      }
    };

    /**
     * Handles navigation to Edit Design view
     */
    const handleEditDesignClick = useCallback(() => {
      handleOpen(PANEL_HEIGHTS[SwipePanelView.EditDesign]);
      navigateTo("pages.edit");
      setIsEditDesignActive(true);
      setIsEventDetailsActive(false);
    }, [navigateTo, handleOpen]);

    /**
     * Handles navigation to Event Details view (main pages view)
     */
    const handleEventDetailsClick = useCallback(() => {
      handleOpen(FULL_HEIGHT);
      navigateTo("pages");
      setIsEventDetailsActive(true);
      setIsEditDesignActive(false);
    }, [navigateTo, handleOpen]);

    /**
     * Handles the end of a swipe gesture and determines the final panel state.
     * Swipe up: opens the panel if it's currently closed or partially open.
     * Swipe down: closes the panel if it's currently open or partially open.
     */
    const handleSwipeEnd = useCallback(
      (swipeDirection: SwipeDirection) => {
        if (swipeDirection === SwipeDirection.Up) {
          if (
            displayState === PanelDisplayState.Closed ||
            displayState === PanelDisplayState.Partial
          ) {
            // Panel is CLOSED or PARTIALLY OPEN → open it
            handleOpen(FULL_HEIGHT);
          }

          return;
        }

        if (swipeDirection === SwipeDirection.Down) {
          if (
            displayState === PanelDisplayState.Open ||
            displayState === PanelDisplayState.Partial
          ) {
            // Panel is OPEN or PARTIALLY OPEN → close it
            handleClose();
          }
        }
      },
      [displayState, handleClose, handleOpen],
    );

    const { handleScrollableChild, handleStartSwipe, isSwiping } =
      useSwipeGesture({
        allowedDirections: [SwipeDirection.Up, SwipeDirection.Down],
        currentPosition,
        minPosition: 0,
        onPositionChange: setCurrentPosition,
        onSwipeEnd: handleSwipeEnd,
        onSwipingChange,
      });

    const containerStyles = swipePanelSharedStyles.container(
      currentPosition,
      isSwiping,
    );
    const panelTransform = getPanelTransform(currentPosition, isSwiping);

    // Always render the closed state buttons behind the panel
    const closedStateButtons = (
      <div
        css={swipePanelStyles.closedStateContainer}
        onMouseDown={handleStartSwipe}
        onTouchStart={handleStartSwipe}
      >
        <div css={swipePanelStyles.closedStateButtons}>
          <div
            css={swipePanelStyles.buttonContainer}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onTouchStart={(event) => {
              event.stopPropagation();
            }}
          >
            <IconToggleButton
              iconType={Icon.types.palette}
              isOn={isEditDesignActive}
              label="Edit Design"
              modifier="secondary"
              name="edit-design-button"
              onClick={(event) => {
                event.stopPropagation();
                handleEditDesignClick();
              }}
              size="small"
            />
          </div>
          <div
            css={swipePanelStyles.buttonContainer}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onTouchStart={(event) => {
              event.stopPropagation();
            }}
          >
            <IconToggleButton
              iconType={Icon.types.editSquare}
              isOn={isEventDetailsActive}
              label="Open All Details"
              modifier="secondary"
              name="event-details-button"
              onClick={handleEventDetailsClick}
              size="small"
            />
          </div>
        </div>
      </div>
    );

    // Render the full panel when open or partial, or when closed but still visible
    return (
      <>
        {closedStateButtons}

        {displayState === PanelDisplayState.Closed && (
          <div css={swipePanelStyles.addElementButtonContainer}>
            <IconButton
              iconType={Icon.types.add}
              onClick={() =>
                triggerAddNewSection(TRACKING.ButtonAddElementClicked)
              }
              size={IconButton.sizes.extraLarge}
            />
          </div>
        )}

        {/* Render the swipe panel when not fully closed */}
        {displayState !== PanelDisplayState.Closed && (
          <>
            {currentPosition > initialPeek * 1.5 && (
              <div
                css={[
                  swipePanelSharedStyles.scrim,
                  swipePanelSharedStyles.scrimVisible,
                ]}
                onClick={handleClose}
              />
            )}
            <div
              css={containerStyles}
              onTransitionEnd={handleTransitionEnd}
              ref={container}
              style={panelTransform}
            >
              <div
                css={swipePanelSharedStyles.swipeArea}
                onMouseDown={handleStartSwipe}
                onTouchStart={handleStartSwipe}
              >
                <button
                  css={swipePanelStyles.headerButton}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggle();
                  }}
                  type="button"
                >
                  <span css={styleUtils.visuallyHidden}>
                    {displayState === PanelDisplayState.Open
                      ? PanelDisplayState.Closed
                      : PanelDisplayState.Open}{" "}
                    panel
                  </span>
                </button>
              </div>

              <div
                css={[
                  swipePanelSharedStyles.content,
                  displayState === PanelDisplayState.Open &&
                    swipePanelSharedStyles.contentScrollable,
                ]}
                onScroll={handleScrollableChild}
                ref={panelContent}
              >
                <SwipePanelContext.Provider
                  value={{
                    close: handleClose,
                    isOpen: displayState === PanelDisplayState.Open,
                    registerSaveHandler,
                    transitionToPanelView,
                  }}
                >
                  <div css={swipePanelSharedStyles.contentWrapper}>
                    {children}
                  </div>
                </SwipePanelContext.Provider>
              </div>
            </div>
          </>
        )}
      </>
    );
  },
);

SwipePanel.displayName = "SwipePanel";
